import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from "react";

import { cn } from "@aircall/ds";

import type { InputChoiceContextValue } from "../../contexts/InputChoiceContext";
import { useInputChoice } from "../../contexts/useInputChoice";

export interface InputChoiceLabelProps
  extends ComponentPropsWithoutRef<"span"> {
  "data-test"?: string;
}

function pickColorClass({
  isSelected,
  isDisabled,
}: InputChoiceContextValue): string {
  if (isDisabled) {
    return "text-muted-foreground";
  }

  if (isSelected) {
    return "text-primary-foreground";
  }

  return "text-foreground";
}

export const InputChoiceLabel = forwardRef<
  HTMLSpanElement,
  InputChoiceLabel.Props
>(
  (
    componentProps: InputChoiceLabel.Props,
    forwardRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const {
      children,
      className,
      "data-test": dataTest = "input-choice-label",
      ...props
    } = componentProps;
    const { isSelected, isDisabled } = useInputChoice();

    return (
      <span
        ref={forwardRef}
        data-slot="input-choice-label"
        data-test={dataTest}
        className={cn(
          "text-sm font-medium",
          pickColorClass({ isSelected, isDisabled }),
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
);
InputChoiceLabel.displayName = "InputChoiceLabel";

export namespace InputChoiceLabel {
  export type Props = InputChoiceLabelProps;
}
